import type { IService, IServiceActivity } from '@common/interfaces';
import type {
    IAddCarePathServiceConsumerSettings,
    IAddCarePathServiceProviderSettings,
    IAddCarePathServiceSupervisorSettings,
} from '@common/interfaces/addCarePathServicesSettings';
import type { SettingsType, SettingType } from '@common/utils/settings';
import type { IServiceOrganizationSettings } from '../../../../../common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { ValidationErrors } from '../../../../utils/validation';
import type {
    AddRightsSteps,
    ICareProviderAddRightsState,
    IServiceActivitiesWithSettings,
    ProviderService,
} from '../../../interfaces/careProviderAddRightsState';
import type { IEmployment } from '../../../interfaces/employment';
import type { IOrganization } from '../../../interfaces/organization';
import type { IPractitioner } from '../../../interfaces/practitioner';
import type { ServiceWithSettings } from './interfaces/serviceWithSettings';
import type { IServiceActivityRight } from './steps/ChooseRights';
import {
    createServiceConsumerSettings,
    createServiceProviderSettings,
    createServiceSupervisorSettings,
} from './defaultSettingsLists';
import { getServiceEmploymentSettingsLabels } from './utils/serviceEmploymentSettingsUtils';

type SetServiceAction =
    | { service: IService; type: 'setServiceConsumer' }
    | { service: IService; type: 'setServiceProvider' }
    | { service: IService; type: 'setServiceSupervisor' };

export type AddRightsAction =
    | SetServiceAction
    | { activity: IServiceActivityRight; enabled: boolean; type: 'setServiceEmploymentActivity' }
    | { currentStep: AddRightsSteps; type: 'setCurrentStep' }
    | { employment: IEmployment; type: 'setEmployment' }
    | { errors: ValidationErrors; type: 'setErrors' }
    | { organization: IOrganization; type: 'setOrganization' }
    | { practitioner: IPractitioner; type: 'setPractitioner' }
    | { type: 'setServiceReader' }
    | { servicesMissingPreferredServiceConsumers: string[]; type: 'setServicesMissingPreferredServiceConsumers' }
    | { preferredServiceConsumers: string[]; serviceId: string; type: 'updatePreferredServiceConsumers' }
    | { serviceId: string; settings: IAddCarePathServiceConsumerSettings; type: 'updateServiceConsumerSettings' }
    | { serviceOrganizationSettings: IServiceOrganizationSettings; type: 'updateServiceOrganizationSettings' }
    | { serviceId: string; settings: IAddCarePathServiceProviderSettings; type: 'updateServiceProviderSettings' }
    | { serviceId: string; settings: Partial<SettingType>; type: 'updateServiceEmploymentSettings' }
    | {
          serviceId: string;
          settings: IAddCarePathServiceSupervisorSettings & { id: string };
          type: 'updateServiceSupervisorSettings';
      };

export function careProviderAddRightsReducer(
    state: ICareProviderAddRightsState,
    action: AddRightsAction
): ICareProviderAddRightsState {
    switch (action.type) {
        case 'setCurrentStep': {
            return {
                ...state,
                currentStep: action.currentStep,
            };
        }
        case 'setOrganization': {
            return {
                ...state,
                organization: action.organization,
            };
        }
        case 'setEmployment': {
            return {
                ...state,
                employment: action.employment,
            };
        }
        case 'setPractitioner': {
            return {
                ...state,
                practitioner: action.practitioner,
            };
        }

        case 'setServiceEmploymentActivity': {
            const newState = { ...state };
            newState.serviceEmploymentActivities = setServicActivityRight(
                action.activity,
                newState.serviceEmploymentActivities,
                action.enabled
            );

            return newState;
        }

        case 'setServiceConsumer': {
            const newState = { ...state };
            newState.consumerServices = setServiceRight<IAddCarePathServiceConsumerSettings>(
                action,
                newState.consumerServices
            );

            return sanitizeState(newState);
        }
        case 'setServiceProvider': {
            const newState = { ...state };
            newState.providerServices = setServiceRight<IAddCarePathServiceProviderSettings>(
                action,
                newState.providerServices
            );

            return sanitizeState(newState);
        }
        case 'setServiceSupervisor': {
            const newState = { ...state };
            newState.supervisorServices = setServiceRight<IAddCarePathServiceSupervisorSettings & { id: string }>(
                action,
                newState.supervisorServices
            );

            return sanitizeState(newState);
        }
        case 'setServiceReader': {
            return {
                ...state,
                addServiceReader: !state.addServiceReader,
            };
        }
        case 'updateServiceConsumerSettings': {
            const newState = { ...state };
            newState.consumerServices = updateServiceRightSettings<IAddCarePathServiceConsumerSettings>(
                action.serviceId,
                action.settings,
                newState.consumerServices
            );

            return newState;
        }

        case 'updateServiceEmploymentSettings': {
            const updatedServiceEmploymentActivities = updateServiceEmploymentActivities(
                action.serviceId,
                action.settings,
                [...state.serviceEmploymentActivities]
            );

            return { ...state, serviceEmploymentActivities: updatedServiceEmploymentActivities };
        }
        case 'updateServiceProviderSettings': {
            const newState = { ...state };
            newState.providerServices = updateServiceRightSettings<IAddCarePathServiceProviderSettings>(
                action.serviceId,
                action.settings,
                newState.providerServices
            );

            return newState;
        }
        case 'updateServiceSupervisorSettings': {
            const newState = { ...state };
            newState.supervisorServices = updateServiceRightSettings<
                IAddCarePathServiceSupervisorSettings & { id: string }
            >(action.serviceId, action.settings, newState.supervisorServices);

            return newState;
        }
        case 'updatePreferredServiceConsumers': {
            const newState = { ...state };
            newState.providerServices = updatePreferredServiceConsumers(
                action.serviceId,
                action.preferredServiceConsumers,
                newState.providerServices
            );

            return newState;
        }
        case 'setServicesMissingPreferredServiceConsumers': {
            const newState = { ...state };
            newState.servicesMissingPreferredServiceConsumers = action.servicesMissingPreferredServiceConsumers;

            return newState;
        }
        case 'setErrors': {
            return {
                ...state,
                errors: action.errors,
            };
        }
        case 'updateServiceOrganizationSettings': {
            return {
                ...state,
                serviceOrganizationSettings: updateServiceOrganizationSettings(
                    action.serviceOrganizationSettings,
                    state.serviceOrganizationSettings
                ),
            };
        }
        default:
            return state;
    }

    function sanitizeState(addRightsState: ICareProviderAddRightsState) {
        const selectedServiceIds = [
            ...addRightsState.consumerServices.map((s) => s.id),
            ...addRightsState.providerServices.map((s) => s.id),
            ...addRightsState.supervisorServices.map((s) => s.id),
        ];

        if (addRightsState.serviceOrganizationSettings.length) {
            addRightsState.serviceOrganizationSettings = addRightsState.serviceOrganizationSettings.filter((sos) =>
                selectedServiceIds.includes(sos.serviceId)
            );
        }

        if (addRightsState.servicesMissingPreferredServiceConsumers) {
            addRightsState.servicesMissingPreferredServiceConsumers =
                addRightsState.servicesMissingPreferredServiceConsumers.filter((id) =>
                    state.providerServices.map((s) => s.id).includes(id)
                );
        }

        return { ...addRightsState };
    }

    function setServiceRight<T>(serviceAction: SetServiceAction, services: ServiceWithSettings<T>[]) {
        const index = services.findIndex((right) => serviceAction.service.id === right.id);

        if (index === -1) {
            services.push({ ...serviceAction.service, settings: createServiceRightSettings<T>(serviceAction) });
        } else {
            services.splice(index, 1);
        }

        return services;
    }

    function setServicActivityRight(
        serviceActivityRight: IServiceActivityRight,
        currentServiceActivities: ICareProviderAddRightsState['serviceEmploymentActivities'],
        enabled: boolean
    ): ICareProviderAddRightsState['serviceEmploymentActivities'] {
        const isServiceAdded = currentServiceActivities.some((service) => service.id === serviceActivityRight.id);
        if (!isServiceAdded && enabled) {
            return [
                ...currentServiceActivities,
                {
                    id: serviceActivityRight.id,
                    serviceActivities: [{ ...serviceActivityRight.activity }],
                },
            ];
        }

        const serviceToUpdate = currentServiceActivities.filter((service) => service.id === serviceActivityRight.id)[0];
        const serviceWithActivityExists = serviceToUpdate.serviceActivities.some(
            (activity) => activity.id === serviceActivityRight.activity.id
        );
        if (!serviceWithActivityExists && enabled) {
            const updatedService = {
                ...serviceToUpdate,
                serviceActivities: [...serviceToUpdate.serviceActivities, { ...serviceActivityRight.activity }],
            };

            return currentServiceActivities.map((service) => {
                return service.id === updatedService.id ? updatedService : service;
            });
        }

        if (serviceToUpdate.serviceActivities.length > 1) {
            const updatedService = getUpdatedServiceWithActivities(serviceToUpdate, serviceActivityRight, enabled);

            return currentServiceActivities.map((service) =>
                service.id === updatedService.id ? updatedService : service
            );
        } else {
            if (!enabled) {
                return currentServiceActivities.filter((service) => service.id !== serviceActivityRight.id);
            }
            const updatedService = getUpdatedServiceWithActivities(serviceToUpdate, serviceActivityRight, enabled);

            return currentServiceActivities.map((service) =>
                service.id === updatedService.id ? updatedService : service
            );
        }
    }

    function getUpdatedServiceWithActivities(
        serviceWithActivities: IServiceActivitiesWithSettings,
        serviceActivityRight: IServiceActivityRight,
        enabled: boolean
    ) {
        const updatedServiceActivities = serviceWithActivities.serviceActivities.filter(
            (activity) => activity.id !== serviceActivityRight.activity.id
        );

        if (enabled) {
            updatedServiceActivities.push(serviceActivityRight.activity);
        }

        const updatedSettings = updateSettingsForUpdatedActivities(
            updatedServiceActivities,
            serviceWithActivities.settings
        );

        return {
            ...serviceWithActivities,
            serviceActivities: updatedServiceActivities,
            settings: updatedSettings,
        };
    }

    function updateSettingsForUpdatedActivities(activities: IServiceActivity[], settings?: Partial<SettingsType>) {
        if (settings) {
            const settingLabels = getServiceEmploymentSettingsLabels(
                activities.map((activity) => activity.customControlCode)
            );

            return settingLabels.reduce((newSettingsObject, settingName) => {
                return {
                    ...newSettingsObject,
                    [settingName]: settings[settingName],
                };
            }, {});
        }
    }

    function updateServiceEmploymentActivities(
        serviceId: string,
        settings: Partial<SettingType>,
        services: IServiceActivitiesWithSettings[]
    ): IServiceActivitiesWithSettings[] {
        const updatedServicesWithActivities = services.map((serviceWithActivitiesAndSettings) => {
            if (serviceWithActivitiesAndSettings.id === serviceId) {
                return {
                    ...serviceWithActivitiesAndSettings,
                    settings,
                } as IServiceActivitiesWithSettings;
            }

            return serviceWithActivitiesAndSettings;
        });

        return updatedServicesWithActivities;
    }

    function updateServiceRightSettings<T>(serviceId: string, settings: T, services: ServiceWithSettings<T>[]) {
        const index = services.findIndex((right) => serviceId === right.id);

        if (index === -1) {
            return services;
        } else {
            services[index].settings = settings;
        }

        return services;
    }

    function updatePreferredServiceConsumers(
        serviceId: string,
        preferredServiceConsumers: string[],
        services: ProviderService[]
    ) {
        const service = services.find((serviceRight) => serviceRight.id === serviceId);

        if (service) {
            service.preferredServiceOrganizationConsumers = preferredServiceConsumers;
        }

        return services;
    }

    function updateServiceOrganizationSettings(
        serviceOrganizationSetting: IServiceOrganizationSettings,
        serviceOrganizationSettings: IServiceOrganizationSettings[]
    ) {
        const existingSettingsIndex = serviceOrganizationSettings.findIndex(
            (sos) => sos.serviceId === serviceOrganizationSetting.serviceId
        );

        if (existingSettingsIndex === -1) {
            serviceOrganizationSettings.push(serviceOrganizationSetting);
        } else {
            serviceOrganizationSettings[existingSettingsIndex] = { ...serviceOrganizationSetting };
        }

        return serviceOrganizationSettings;
    }

    function createServiceRightSettings<T>(serviceAction: SetServiceAction) {
        let settings;
        if (serviceAction.type === 'setServiceConsumer') {
            settings = createServiceConsumerSettings(serviceAction.service);
        }

        if (serviceAction.type === 'setServiceProvider') {
            settings = createServiceProviderSettings(serviceAction.service);
        }

        if (serviceAction.type === 'setServiceSupervisor') {
            settings = createServiceSupervisorSettings(serviceAction.service);
        }

        return settings as unknown as T;
    }
}
